(function () {
  if (window.FlowSwipeCarousel) return;

  var instances = new WeakMap();

  function toArray(value) {
    return Array.prototype.slice.call(value || []);
  }

  function addListener(target, eventName, handler, options, cleanup) {
    if (!target) return;

    target.addEventListener(eventName, handler, options);
    cleanup.push(function () {
      target.removeEventListener(eventName, handler, options);
    });
  }

  function getGalleryEdgeOffset(gallery) {
    var propertyName = gallery.dataset.carouselEdgeOffsetProperty || '--gallery-edge-offset';
    var value = window.getComputedStyle(gallery).getPropertyValue(propertyName);
    var number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : 0;
  }

  function getSlideScrollLeft(gallery, slide) {
    return slide.offsetLeft - getGalleryEdgeOffset(gallery);
  }

  function getControlIndex(control, fallbackIndex, config) {
    if (typeof config.getControlIndex === 'function') {
      var customIndex = config.getControlIndex(control, fallbackIndex);
      if (Number.isFinite(customIndex)) return customIndex;
    }

    var dataIndex = Number.parseInt(control.dataset.carouselIndex || '', 10);
    return Number.isFinite(dataIndex) ? dataIndex : fallbackIndex;
  }

  function createPaginationButton(index, config) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = config.pageClassName || '';
    button.setAttribute('aria-label', (config.pageLabelPrefix || 'View image ') + (index + 1));
    return button;
  }

  function init(config) {
    if (!config || !config.gallery) return null;

    var gallery = config.gallery;
    var existing = instances.get(gallery);
    if (existing) existing.destroy();

    var track = config.track;
    var slides = toArray(config.slides);
    if (!track || !slides.length) return null;

    var cleanup = [];
    var activeClass = config.activeClass || 'is-active';
    var controlActiveClass = config.controlActiveClass || activeClass;
    var staticClass = config.staticClass || 'is-static';
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var activeSlideIndex = -1;
    var pointerStart = null;
    var dragThreshold = config.dragThreshold || 8;
    var galleryTrackHeight = 0;
    var scrollFrame = null;
    var controls = toArray(config.controls);

    if (config.pagination) {
      config.pagination.innerHTML = '';
      controls = slides.map(function (_slide, index) {
        var button = createPaginationButton(index, config);
        config.pagination.appendChild(button);
        return button;
      });
    }

    function setPaginationVisibility(isStatic) {
      if (!config.pagination) return;
      config.pagination.hidden = isStatic;
    }

    function setActivePage(activeIndex, options) {
      var slide = slides[activeIndex];
      if (!slide) return false;

      var didChange = activeIndex !== activeSlideIndex;
      activeSlideIndex = activeIndex;

      slides.forEach(function (item, index) {
        item.classList.toggle(activeClass, index === activeIndex);
      });

      controls.forEach(function (control, index) {
        var controlIndex = getControlIndex(control, index, config);
        var isActive = controlIndex === activeIndex;
        control.classList.toggle(controlActiveClass, isActive);

        if (isActive) {
          control.setAttribute('aria-current', 'true');
        } else {
          control.removeAttribute('aria-current');
        }
      });

      if (didChange && (!options || !options.silent) && typeof config.onActiveChange === 'function') {
        config.onActiveChange(activeIndex, slide);
      }

      return didChange;
    }

    function scrollToSlide(index, options) {
      var slide = slides[index];
      if (!slide) return false;

      track.scrollTo({
        left: getSlideScrollLeft(gallery, slide),
        behavior: reduceMotion || (options && options.instant) ? 'auto' : 'smooth',
      });

      setActivePage(index, options);
      updateGalleryPeekState();
      return true;
    }

    function updateGalleryFadeMetrics() {
      var trackHeight = Math.round(track.getBoundingClientRect().height);
      if (!trackHeight || trackHeight === galleryTrackHeight) return;

      galleryTrackHeight = trackHeight;
      gallery.style.setProperty('--gallery-track-height', trackHeight + 'px');
    }

    function updateGalleryPeekState() {
      if (slides.length <= 1) {
        gallery.classList.remove('has-prev-peek', 'has-next-peek');
        return;
      }

      var trackRect = track.getBoundingClientRect();
      var threshold = 1;
      var hasPrevPeek = false;
      var hasNextPeek = false;

      slides.forEach(function (slide) {
        var slideRect = slide.getBoundingClientRect();

        if (slideRect.left < trackRect.left - threshold && slideRect.right > trackRect.left + threshold) {
          hasPrevPeek = true;
        }

        if (slideRect.left < trackRect.right - threshold && slideRect.right > trackRect.right + threshold) {
          hasNextPeek = true;
        }
      });

      gallery.classList.toggle('has-prev-peek', hasPrevPeek);
      gallery.classList.toggle('has-next-peek', hasNextPeek);
    }

    function updateActivePage() {
      scrollFrame = null;

      var edgeOffset = getGalleryEdgeOffset(gallery);
      var activeIndex = 0;
      var activeDistance = Infinity;

      slides.forEach(function (slide, index) {
        var distance = Math.abs(slide.offsetLeft - track.scrollLeft - edgeOffset);
        if (distance < activeDistance) {
          activeDistance = distance;
          activeIndex = index;
        }
      });

      setActivePage(activeIndex);
      updateGalleryFadeMetrics();
      updateGalleryPeekState();
    }

    function requestActiveUpdate() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(updateActivePage);
    }

    controls.forEach(function (control, index) {
      addListener(control, 'click', function () {
        scrollToSlide(getControlIndex(control, index, config));
      }, false, cleanup);
    });

    if (slides.length <= 1) {
      gallery.classList.add(staticClass);
      setPaginationVisibility(true);
      setActivePage(0, { silent: true });
      updateGalleryFadeMetrics();
      updateGalleryPeekState();
    } else {
      gallery.classList.remove(staticClass);
      setPaginationVisibility(false);

      addListener(track, 'scroll', requestActiveUpdate, { passive: true }, cleanup);
      addListener(window, 'resize', updateActivePage, false, cleanup);

      slides.forEach(function (slide, index) {
        addListener(slide, 'pointerdown', function (event) {
          if (event.button !== undefined && event.button !== 0) return;
          pointerStart = {
            x: event.clientX,
            y: event.clientY,
            slide: index,
          };
        }, { passive: true }, cleanup);

        addListener(slide, 'pointercancel', function () {
          pointerStart = null;
        }, false, cleanup);

        addListener(slide, 'click', function (event) {
          if (!pointerStart || pointerStart.slide !== index) return;

          var deltaX = Math.abs(event.clientX - pointerStart.x);
          var deltaY = Math.abs(event.clientY - pointerStart.y);
          pointerStart = null;

          if (deltaX > dragThreshold || deltaY > dragThreshold || index === activeSlideIndex) return;

          scrollToSlide(index);
        }, false, cleanup);
      });

      setActivePage(0, { silent: true });
      updateActivePage();
    }

    var api = {
      refresh: updateActivePage,
      scrollToSlide: scrollToSlide,
      destroy: function () {
        cleanup.forEach(function (callback) {
          callback();
        });
        if (scrollFrame) {
          window.cancelAnimationFrame(scrollFrame);
        }
        instances.delete(gallery);
      },
    };

    instances.set(gallery, api);
    return api;
  }

  window.FlowSwipeCarousel = {
    init: init,
  };

  window.dispatchEvent(new CustomEvent('flow-swipe-carousel:ready'));
})();
